import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CancelPresentationOutlinedIcon from "@mui/icons-material/CancelPresentationOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import FilterDramaIcon from "@mui/icons-material/FilterDrama";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import DateRangeFilter from "./DateRangeFilter";
import { updateJob } from "../../api/jobs";
import { addJobsToAssignmentPeriod } from "../../api/jobAssignmentPeriods";
import { getRoomRestrictionColor } from "../../utils/roomRestriction";

const TABLE_MIN_HEIGHT_10_ROWS = 700;
const BULK_NO_CHANGE = "__no_change__";
const JOB_STATUS_OPTIONS = [
  "pending",
  "assigned",
  "in_progress",
  "completed",
  "failed",
  "cancelled",
];

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusTone = (status) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return { background: "#fff3cd", color: "#785900" };
    case "assigned":
      return { background: "#dbeeff", color: "#174b80" };
    case "in_progress":
      return { background: "#d9f7ef", color: "#0f624d" };
    case "completed":
      return { background: "#dff4ea", color: "#21663f" };
    case "failed":
    case "cancelled":
      return { background: "#fde4e1", color: "#8c2d2d" };
    default:
      return { background: "#edf1f4", color: "#425466" };
  }
};

const getJobLocationLabel = (job) => {
  return (
    job?.location_name ||
    job?.location?.location_name ||
    job?.location?.name ||
    job?.building_name ||
    job?.room_name ||
    job?.location_address ||
    job?.location ||
    job?.site_name ||
    job?.address ||
    "-"
  );
};

const normalizeSortValue = (job, sortKey) => {
  switch (sortKey) {
    case "job":
      return String(job.job_number || job.pk_id || "").toLowerCase();
    case "status":
      return String(job.status || "").toLowerCase();
    case "priority":
      return String(job.priority || "").toLowerCase();
    case "technician":
      return String(
        job.assigned_technician_name || job.assigned_technician_id || "",
      ).toLowerCase();
    case "location":
      return String(getJobLocationLabel(job)).toLowerCase();
    case "customer":
      return String(job.customer_name || job.customer_id || "").toLowerCase();
    case "service":
      return String(
        job.service_type_name || job.service_type_id || "",
      ).toLowerCase();
    case "start":
      return job.scheduled_start ? new Date(job.scheduled_start).getTime() : 0;
    case "end":
      return job.scheduled_end ? new Date(job.scheduled_end).getTime() : 0;
    case "tasks":
      return Number(job.total_job_tasks) || job.tasks?.length || 0;
    default:
      return "";
  }
};

export default function JobsQueue({
  jobs = [],
  selectedJobId,
  selectedJobPeriods = [],
  jobPeriodsByJobId = {},
  onSelectJob,
  onJobDoubleTap,
  onCreateJob,
  search,
  onSearchChange,
  rangeStart,
  rangeEnd,
  onRangeStartChange,
  onRangeEndChange,
  onClearRange,
  onRefreshCurrentRange,
  rangeRefreshing = false,
  quickFilter,
  onQuickFilterChange,
  quickFilterCounts,
  onClearQuickFilter,
  hasPeriodScope = false,
  periodScopeLabel = "",
  onClearPeriodScope,
  hideCustomerContext = false,
  pickedRoute = null,
  onStartRoutePicker,
  onClearPickedRoute,
  onBulkJobsUpdated,
  onFeedback,
  onSelectionChange,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("start");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(BULK_NO_CHANGE);
  const [bulkBusy, setBulkBusy] = useState(false);
  const enableBulkActions = selectedJobIds.length > 0 && !bulkBusy;
  const visibleColumns = hideCustomerContext
    ? [
        ["job", "Job"],
        ["route", "Route"],
        ["status", "Status"],
        ["technician", "Technician"],
        ["tasks", "Info"],
        ["start", "Start/End"],
      ]
    : [
        ["job", "Job"],
        ["status", "Status"],
        ["tasks", "Info"],
        ["technician", "Technician"],
        ["Assignment", "Assignment"],
        ["customer", "Customer/Location"],

        ["start", "Start/End"],
      ];

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnKey);
    setSortDirection("asc");
  };

  const sortedJobs = useMemo(() => {
    const directionMultiplier = sortDirection === "asc" ? 1 : -1;

    return [...jobs].sort((left, right) => {
      const leftValue = normalizeSortValue(left, sortBy);
      const rightValue = normalizeSortValue(right, sortBy);

      if (leftValue < rightValue) return -1 * directionMultiplier;
      if (leftValue > rightValue) return 1 * directionMultiplier;
      return 0;
    });
  }, [jobs, sortBy, sortDirection]);

  const paginatedJobs = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedJobs.slice(start, start + rowsPerPage);
  }, [sortedJobs, page, rowsPerPage]);

  useEffect(() => {
    if (jobs.length === 0) {
      return;
    }

    const selectedExists = jobs.some(
      (job) => String(job.pk_id) === String(selectedJobId),
    );

    if (!selectedExists && onSelectJob) {
      onSelectJob(sortedJobs[0].pk_id);
    }
  }, [jobs, onSelectJob, selectedJobId, sortedJobs]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(jobs.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [jobs.length, page, rowsPerPage]);

  const handlePageChange = (_, nextPage) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectedJobIdsSet = useMemo(
    () => new Set(selectedJobIds.map(String)),
    [selectedJobIds],
  );
  const paginatedJobIds = useMemo(
    () => paginatedJobs.map((job) => String(job.pk_id)),
    [paginatedJobs],
  );
  const allVisibleSelected =
    paginatedJobIds.length > 0 &&
    paginatedJobIds.every((jobId) => selectedJobIdsSet.has(jobId));
  const someVisibleSelected = paginatedJobIds.some((jobId) =>
    selectedJobIdsSet.has(jobId),
  );
  const selectedJobCount = selectedJobIds.length;

  useEffect(() => {
    onSelectionChange?.(selectedJobIds);
  }, [onSelectionChange, selectedJobIds]);

  const handleToggleJobSelection = (jobId) => {
    setSelectedJobIds((prev) => {
      const nextSet = new Set(prev.map(String));
      const id = String(jobId);
      if (nextSet.has(id)) {
        nextSet.delete(id);
      } else {
        nextSet.add(id);
      }
      return Array.from(nextSet);
    });
  };

  const handleJobRowClick = (jobId) => {
    onSelectJob?.(jobId);
    handleToggleJobSelection(jobId);
  };

  const handleToggleAllVisibleJobs = (event) => {
    const checked = Boolean(event.target.checked);
    const nextIds = checked
      ? Array.from(new Set([...selectedJobIds.map(String), ...paginatedJobIds]))
      : selectedJobIds.filter(
          (jobId) => !paginatedJobIds.includes(String(jobId)),
        );

    setSelectedJobIds(nextIds);
  };

  const handleBulkApply = async () => {
    const ids = Array.from(new Set(selectedJobIds.map(String).filter(Boolean)));
    if (ids.length === 0) {
      return;
    }

    const changeStatus = bulkStatus !== BULK_NO_CHANGE;
    const changePeriod = Boolean(pickedRoute);

    if (!changeStatus && !changePeriod) {
      onFeedback?.(
        "Choose a bulk status or assign a route to apply.",
        "warning",
      );
      return;
    }

    try {
      setBulkBusy(true);

      if (changeStatus) {
        for (const jobId of ids) {
          await updateJob(jobId, { status: bulkStatus });
        }
      }

      if (changePeriod) {
        await addJobsToAssignmentPeriod(pickedRoute.periodId, ids, {
          sync_assignments: true,
        });
      }

      if (onBulkJobsUpdated) {
        await onBulkJobsUpdated();
      }

      setSelectedJobIds([]);
      setBulkStatus(BULK_NO_CHANGE);
      onClearPickedRoute?.();
      onFeedback?.(`Updated ${ids.length} job(s).`, "success");
    } catch (err) {
      onFeedback?.(err.message || "Failed to update selected jobs", "error");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: "#f5f8fa",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Jobs Queue
        </Typography>
        <Tooltip title="Create job">
          <span>
            <IconButton size="small" onClick={onCreateJob}>
              <AddCircleOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.2,
          pt: "16px",
          borderBottom: "1px solid",
          borderColor: "divider",
          "& .MuiTextField-root": {
            "& .MuiInputLabel-root": {
              fontSize: 12,
            },
            "& .MuiInputBase-root": {
              minHeight: 34,
              fontSize: 12,
            },
            "& .MuiInputBase-input": {
              py: 0.65,
            },
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <DateRangeFilter
            start={rangeStart}
            end={rangeEnd}
            onStartChange={onRangeStartChange}
            onEndChange={onRangeEndChange}
            onClear={onClearRange}
          />

          <TextField
            label="Job Search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={
              hideCustomerContext
                ? "Job number, device"
                : "Job number, customer, device"
            }
            size="small"
            sx={{ width: 240, flexShrink: 0 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ width: 28 }}>
                  <Tooltip title={search.length > 0 ? "Clear search" : ""}>
                    <span style={{ display: "inline-flex", width: 28 }}>
                      <IconButton
                        size="small"
                        onClick={() => onSearchChange("")}
                        disabled={search.length === 0}
                        aria-label="Clear job search"
                        sx={{
                          mr: -0.5,
                          visibility: search.length > 0 ? "visible" : "hidden",
                        }}
                      >
                        <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh jobs queue">
            <span>
              <IconButton
                size="small"
                onClick={onRefreshCurrentRange}
                disabled={rangeRefreshing || (!rangeStart && !rangeEnd)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                {rangeRefreshing ? (
                  <CircularProgress size={18} />
                ) : (
                  <SyncOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {/*Filters section*/}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1}
          sx={{ alignItems: { lg: "center" }, minWidth: 0, mt: 1 }}
        >
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                gap: 0.6,
                flexWrap: "nowrap",
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "100%",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {hasPeriodScope ? (
                <Chip
                  label={periodScopeLabel || "Route Scope"}
                  size="small"
                  color="warning"
                  sx={{
                    height: 24,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              ) : null}
              {[
                ["unassigned", "Unassigned"],
                ["assigned", "Assigned"],
                ["in_progress", "In Progress"],
                ["completed", "Completed"],
                ["on_hold", "On Hold"],
                ["failed", "Failed"],
              ].map(([key, label]) => {
                const isActive = quickFilter === key;
                return (
                  <Button
                    key={key}
                    size="small"
                    variant={isActive ? "contained" : "outlined"}
                    onClick={() => onQuickFilterChange(key)}
                    sx={{
                      minHeight: 24,
                      px: 0.8,
                      py: 0.2,
                      textTransform: "none",
                      borderRadius: 1.5,
                      fontSize: 10,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    {`${quickFilterCounts?.[key] || 0} ${label}`}
                  </Button>
                );
              })}
            </Box>

            <Button
              size="small"
              variant="text"
              onClick={() => {
                onClearQuickFilter && onClearQuickFilter();
                onClearPeriodScope && onClearPeriodScope();
              }}
              disabled={quickFilter === "all" && !hasPeriodScope}
              sx={{
                minHeight: 24,
                px: 0.6,
                py: 0.2,
                textTransform: "none",
                fontSize: 10,
                whiteSpace: "nowrap",
              }}
            >
              Reset Filter
            </Button>
          </Stack>
        </Stack>
      </Box>
      {/* Bulk selection action bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{
          mx: 2,
          mt: 1.5,
          p: 1.25,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "#f8fafc",
          "& .MuiTextField-root": {
            "& .MuiInputLabel-root": {
              fontSize: 12,
            },
            "& .MuiInputBase-root": {
              minHeight: 34,
              fontSize: 12,
            },
            "& .MuiInputBase-input": {
              py: 0.65,
            },
          },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {selectedJobCount} job(s) selected
        </Typography>
        <TextField
          select
          size="small"
          label="Bulk status"
          value={bulkStatus}
          onChange={(event) => setBulkStatus(event.target.value)}
          sx={{ minWidth: 120 }}
          disabled={!enableBulkActions}
        >
          <MenuItem value={BULK_NO_CHANGE} sx={{ fontSize: 12 }}>
            No change
          </MenuItem>
          {JOB_STATUS_OPTIONS.map((status) => (
            <MenuItem key={status} value={status} sx={{ fontSize: 12 }}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="outlined"
          onClick={onStartRoutePicker}
          disabled={!enableBulkActions}
          sx={{
            minHeight: 34,
            textTransform: "none",
            whiteSpace: "nowrap",
            fontSize: 12,
          }}
        >
          Assign Route
        </Button>
        <TextField
          size="small"
          label="Route"
          value={
            pickedRoute ? `#${pickedRoute.periodId} ${pickedRoute.label}` : ""
          }
          placeholder="No route selected"
          InputProps={{ readOnly: true }}
          sx={{ minWidth: 150 }}
        />
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <Tooltip title="Apply changes to selected jobs">
            <span>
              <IconButton
                size="small"
                color="success"
                onClick={handleBulkApply}
                disabled={
                  bulkBusy || (bulkStatus === BULK_NO_CHANGE && !pickedRoute)
                }
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                {bulkBusy ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear selection">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setSelectedJobIds([]);
                  setBulkStatus(BULK_NO_CHANGE);
                  onClearPickedRoute?.();
                }}
                disabled={!enableBulkActions}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                <CancelPresentationOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <TablePagination
        component="div"
        count={jobs.length}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[]}
        showFirstButton
        showLastButton
      />

      <TableContainer
        sx={{
          minHeight: TABLE_MIN_HEIGHT_10_ROWS,
          maxHeight: TABLE_MIN_HEIGHT_10_ROWS,
          overflow: "auto",
        }}
      >
        <Table stickyHeader size="small" aria-label="jobs queue table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 42 }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={handleToggleAllVisibleJobs}
                  onClick={(event) => event.stopPropagation()}
                />
              </TableCell>
              {visibleColumns.map(([key, label]) => (
                <TableCell
                  key={key}
                  sortDirection={sortBy === key ? sortDirection : false}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy === key}
                    direction={sortBy === key ? sortDirection : "asc"}
                    onClick={() => handleSort(key)}
                    sx={{ fontWeight: 700 }}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} sx={{ py: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    No jobs match the current queue filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedJobs.map((job) => {
                const tone = getStatusTone(job.status);
                const isSelected = String(selectedJobId) === String(job.pk_id);
                const isMultiSelected = selectedJobIdsSet.has(
                  String(job.pk_id),
                );
                const taskTotal = Number(job.total_job_tasks) || 0;
                const taskCompleted =
                  Number(job.job_tasks_completed) +
                    Number(job.job_tasks_failed) || 0;
                const taskFailed = Number(job.job_tasks_failed) || 0;
                const routePeriods =
                  jobPeriodsByJobId[String(job.pk_id)] ||
                  (isSelected ? selectedJobPeriods : []);

                return (
                  <React.Fragment key={job.pk_id}>
                    {/* Customer Context */}
                    {hideCustomerContext ? (
                      <TableRow
                        key={job.pk_id}
                        hover
                        selected={isSelected || isMultiSelected}
                        onClick={() => handleJobRowClick(job.pk_id)}
                        onDoubleClick={() =>
                          onJobDoubleTap && onJobDoubleTap(job.pk_id)
                        }
                        sx={{
                          cursor: "pointer",
                          "& .job-row-actions": {
                            opacity: isSelected ? 1 : 0,
                            transition: "opacity 120ms ease",
                          },
                          "&:hover .job-row-actions": { opacity: 1 },
                        }}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedJobIdsSet.has(String(job.pk_id))}
                            onChange={() => handleToggleJobSelection(job.pk_id)}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: getRoomRestrictionColor(
                                job.room_restriction,
                              ),
                            }}
                            noWrap
                          >
                            {job.device_name || "No device"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {job.job_number || `Job ${job.pk_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {job.route_name || job.assignment_period_id ? (
                              <Chip
                                key={job.route_name || job.assignment_period_id}
                                size="small"
                                variant="outlined"
                                label={`#${job.assignment_period_id || ""}${job.route_name ? ` - ${job.route_name}` : ""}`}
                                sx={{ maxWidth: 220 }}
                              />
                            ) : isSelected ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Not linked to any assignment period.
                              </Typography>
                            ) : (
                              <Chip
                                size="small"
                                label="Unassigned"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={job.status || "unknown"}
                            size="small"
                            sx={{
                              backgroundColor: tone.background,
                              color: tone.color,
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{
                              display: "block",
                              maxWidth: 110,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {job.assigned_technician_name || "Unassigned"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Tasks Failed">
                            <Chip
                              label={`${taskFailed}`}
                              size="small"
                              variant="outlined"
                              color={taskFailed > 0 ? "error" : "primary"}
                              sx={{ marginRight: 0.4 }}
                            />
                          </Tooltip>
                          <Tooltip title="Task Progress">
                            <Chip
                              label={`${taskCompleted}/${taskTotal}`}
                              size="small"
                              variant="outlined"
                              color={taskTotal > 0 ? "primary" : "warning"}
                              sx={{ marginRight: 0.4 }}
                            />
                          </Tooltip>
                          <Tooltip title="Current priority of this job">
                            <Chip
                              label={job.priority || "medium"}
                              size="small"
                              variant="outlined"
                              color={
                                job.priority === "high" ||
                                job.priority === "urgent"
                                  ? "error"
                                  : job.priority === "medium"
                                    ? "warning"
                                    : "default"
                              }
                            />
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            fontSize: 12,
                            position: "relative",
                          }}
                        >
                          {formatDateTime(job.scheduled_start)} /{" "}
                          {formatDateTime(job.scheduled_end)}
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            className="job-row-actions"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              right: 6,
                              transform: "translateY(-50%)",
                              px: 0.25,
                              borderRadius: 1.5,
                              backgroundColor: "rgba(255, 255, 255, 0.86)",
                              boxShadow: isSelected
                                ? "0 4px 12px rgba(12, 30, 37, 0.14)"
                                : "none",
                              ".MuiTableRow-hover:hover &": {
                                backgroundColor: "rgba(245, 248, 250, 0.92)",
                              },
                            }}
                          >
                            <Tooltip title="Open job editor">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onJobDoubleTap?.(job.pk_id);
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow
                        key={job.pk_id}
                        hover
                        selected={isSelected || isMultiSelected}
                        onClick={() => handleJobRowClick(job.pk_id)}
                        onDoubleClick={() =>
                          onJobDoubleTap && onJobDoubleTap(job.pk_id)
                        }
                        sx={{
                          cursor: "pointer",
                          "& .job-row-actions": {
                            opacity: isSelected ? 1 : 0,
                            transition: "opacity 120ms ease",
                          },
                          "&:hover .job-row-actions": { opacity: 1 },
                          position: "relative",
                        }}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedJobIdsSet.has(String(job.pk_id))}
                            onChange={() => handleToggleJobSelection(job.pk_id)}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              minWidth: 0,
                            }}
                          >
                            <Tooltip
                              title={
                                job.service_type_name === "on-site"
                                  ? job.device_id
                                    ? "Service"
                                    : "Manual"
                                  : "Remote"
                              }
                              placement="top"
                            >
                              {job.service_type_name === "on-site" ? (
                                job.device_id ? (
                                  <SmartToyOutlinedIcon
                                    fontSize="small"
                                    color="action"
                                  />
                                ) : (
                                  <MeetingRoomOutlinedIcon
                                    fontSize="small"
                                    color="action"
                                  />
                                )
                              ) : (
                                <FilterDramaIcon
                                  fontSize="small"
                                  color="action"
                                />
                              )}
                            </Tooltip>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                              noWrap
                            >
                              {job.job_number || `Job ${job.pk_id}`}
                            </Typography>
                          </Box>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {job.device_name ||
                              job.device_id ||
                              job.room_name ||
                              "No device"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={job.status || "unknown"}
                            size="small"
                            sx={{
                              backgroundColor: tone.background,
                              color: tone.color,
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Tasks Failed">
                            <Chip
                              label={`${taskFailed}`}
                              size="small"
                              variant="outlined"
                              color={taskFailed > 0 ? "error" : "primary"}
                              sx={{ marginRight: 0.4 }}
                            />
                          </Tooltip>
                          <Tooltip title="Task Progress">
                            <Chip
                              label={`${taskCompleted}/${taskTotal}`}
                              size="small"
                              variant="outlined"
                              color={taskTotal > 0 ? "primary" : "warning"}
                              sx={{ marginRight: 0.4 }}
                            />
                          </Tooltip>
                          <Tooltip title="Current priority of this job">
                            <Chip
                              label={job.priority || "medium"}
                              size="small"
                              variant="outlined"
                              color={
                                job.priority === "high" ||
                                job.priority === "urgent"
                                  ? "error"
                                  : job.priority === "medium"
                                    ? "warning"
                                    : "default"
                              }
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {job.assigned_technician_name ||
                            job.assigned_technician_id ||
                            "Unassigned"}
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          {routePeriods.length > 0 ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              useFlexGap
                              flexWrap="wrap"
                            >
                              {routePeriods.map((period) => (
                                <Chip
                                  key={`job-membership-${job.pk_id}-${period.pk_id}`}
                                  size="small"
                                  variant="outlined"
                                  label={`#${period.pk_id}` || "default"}
                                  sx={{ maxWidth: 160 }}
                                />
                              ))}
                            </Stack>
                          ) : isSelected ? (
                            <Typography variant="body2" color="text.secondary">
                              Not linked to any assignment period.
                            </Typography>
                          ) : (
                            <Chip
                              size="small"
                              label="Unassigned"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700 }}
                            noWrap
                          >
                            {job.customer_name ||
                              job.customer_id ||
                              "No customer"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {getJobLocationLabel(job)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: 12, fontWeight: 400 }}
                            noWrap
                          >
                            {formatDateTime(job.scheduled_start)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: 12, fontWeight: 400 }}
                            noWrap
                          >
                            {formatDateTime(job.scheduled_end)}
                          </Typography>
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            className="job-row-actions"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              right: 6,
                              transform: "translateY(-50%)",
                              px: 0.25,
                              borderRadius: 1.5,
                              backgroundColor: "rgba(255, 255, 255, 0.86)",
                              boxShadow: isSelected
                                ? "0 4px 12px rgba(12, 30, 37, 0.14)"
                                : "none",
                              zIndex: 1,
                              ".MuiTableRow-hover:hover &": {
                                backgroundColor: "rgba(245, 248, 250, 0.92)",
                              },
                            }}
                          >
                            <Tooltip title="Open job editor">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onJobDoubleTap?.(job.pk_id);
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={jobs.length}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[50, 100]}
        showFirstButton
        showLastButton
      />
    </Paper>
  );
}
